import { chapterConfig, type Chapter } from "@repo/vihangam-yog-shared";

import type { AdminRole } from "../auth/AuthContext";
import type { NavId } from "../components/Shell";

/**
 * The words the team app uses for its pages and access levels, kept in
 * one place so the sidebar, Home, Team access, Your account and the
 * Feedback page picker can never disagree.
 */

/** Page names, as the sidebar and page headings show them. */
export const NAV_LABEL = {
  overview: "Home",
  events: "Events",
  "varanasi-trip": "Varanasi trip",
  reception: "Reception",
  "panel-questions": "Panel questions",
  members: "Members",
  emails: "Emails",
  inquiries: "Website messages",
  "event-feedback": "Event feedback",
  "app-interest": "App waiting list",
  roles: "Team access",
  feedback: "Feedback",
  settings: "Your account",
} satisfies Record<NavId, string>;

const ROLE_LABEL = {
  superadmin: "Organiser",
  volunteer: "Team member",
  // Coordination is retired: an account still holding the old
  // coordinator role has a team member's access and is named as one.
  coordinator: "Team member",
  member: "Member",
} satisfies Record<AdminRole, string>;

/** Someone signed in whom nobody has given access yet. */
const NO_ACCESS_LABEL = "No access";

/** An organiser whose access comes from the email list set when the app
 *  was installed, so it can't be changed on Team access. */
export const BUILT_IN_ORGANISER_LABEL = "Built-in organiser";

/**
 * How an access level is named to people. A volunteer is a "Team member"
 * (they can't see guest details), never an "Organiser". An account that
 * still holds the retired coordinator role reads as a team member on every
 * chapter. Where a chapter has no society membership, a member role opens
 * nothing, so it reads as no access.
 */
export function roleDisplayLabel(
  role: AdminRole | null,
  chapter: Chapter,
): string {
  if (role === null) {
    return NO_ACCESS_LABEL;
  }
  const { features } = chapterConfig(chapter);
  if (role === "member" && !features.includes("members")) {
    return NO_ACCESS_LABEL;
  }
  return ROLE_LABEL[role];
}
